/**
 * @fileoverview Hook to fetch and transform users for User Management.
 *
 * Fetches users based on current user permissions and transforms API response
 * into view models for Sector Admins and Plant Users tables.
 */

import type { MccUser } from "auth";
import { SearchPayload } from "lib/api";
import { MccApi } from "lib/api/mccApiWrapper";
import { UseHasPermission } from "lib/hooks/useHasPermission";
import { UsePermissionRefresh } from "lib/hooks/useSectorsAndPermissions";
import * as log from "lib/log";
import { UserFilter, UserSearchResultDTO } from "models/dto/user";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserSearchSlice } from "store/slices/userSearchSlice";
import { MapUserSearchResultsToViewModels } from "../mappers/userManagementMappers";

/**
 * @returns User management data including sector admins, plant users, loading
 *     state, error, and refetch function.
 */
export function UseUserManagement() {
	const dispatch = useDispatch();
	const permissions = UseHasPermission();
	const { handlePermissionError } = UsePermissionRefresh();

	const users = useSelector(UserSearchSlice.selectors.selectUsers);
	const loading = useSelector(UserSearchSlice.selectors.selectLoading);
	const error = useSelector(UserSearchSlice.selectors.selectError);

	const { sectorAdmins, plantUsers } = useMemo(() => {
		if (users.length === 0) {
			return { sectorAdmins: [], plantUsers: [] };
		}
		return MapUserSearchResultsToViewModels(users, permissions);
	}, [users, permissions]);

	const fetchUsers = useCallback(async () => {
		if (
			!permissions.canViewSectorAdmins &&
			!permissions.canViewPlantUsers
		) {
			dispatch(UserSearchSlice.actions.SetUsers([]));
			return;
		}

		try {
			dispatch(UserSearchSlice.actions.SetLoading(true));
			dispatch(UserSearchSlice.actions.SetError(""));

			const api = MccApi.GetInstance((window as any).store);

			const searchFilters = {
				andOrOperator: "AND" as const,
				filterGroups: [],
				fieldFilters: [],
			};

			const payload: SearchPayload<MccUser, UserFilter> = {
				paging: {} as any,
				resultList: [],
				sortList: [],
				searchFilters,
			};

			const resp = await api.SearchUsers(payload);
			const users: UserSearchResultDTO[] = (resp.resultList ||
				[]) as UserSearchResultDTO[];

			dispatch(UserSearchSlice.actions.SetUsers(users));
			dispatch(UserSearchSlice.actions.SetLoading(false));
		} catch (e: unknown) {
			log.Errorf("Failed to fetch users: %j", e);
			try {
				await handlePermissionError(e);
			} catch (retryError: unknown) {
				const error = retryError as {
					response?: { data?: { message?: string } };
					message?: string;
				};
				const errorMessage =
					error?.response?.data?.message ||
					error?.message ||
					"Failed to load users.";
				dispatch(UserSearchSlice.actions.SetError(errorMessage));
				dispatch(UserSearchSlice.actions.SetUsers([]));
			}
		} finally {
			dispatch(UserSearchSlice.actions.SetLoading(false));
		}
	}, [
		permissions.canViewSectorAdmins,
		permissions.canViewPlantUsers,
		handlePermissionError,
		dispatch,
	]);

	useEffect(() => {
		fetchUsers();
	}, [fetchUsers]);

	return {
		sectorAdmins,
		plantUsers,
		loading,
		error,
		refetch: fetchUsers,
	};
}
